/** Cumulative `inbound-rtp` counters at one `getStats()` poll. */
export type PacketCountSample = {
  atMs: number;
  packetsLost: number;
  packetsReceived: number;
  /** Retransmission requests sent; undefined where the browser does not report it. */
  nackCount?: number;
  /** Keyframe requests sent (Picture Loss Indication); undefined where not reported. */
  pliCount?: number;
};

type StartGraceArgs = {
  connectedAtMs: number;
  nowMs: number;
  graceMs: number;
};

type WindowCountArgs = {
  samples: PacketCountSample[];
  select: (sample: PacketCountSample) => number | undefined;
};

type AddPacketCountSampleArgs = {
  samples: PacketCountSample[];
  sample: PacketCountSample;
  windowMs: number;
};

/**
 * Appends a poll and drops those older than the window, keeping the newest one at or before
 * the window start as the baseline the window is measured from.
 */
export const addPacketCountSample = ({
  samples,
  sample,
  windowMs,
}: AddPacketCountSampleArgs): PacketCountSample[] => {
  const next = [...samples, sample];
  const windowStart = sample.atMs - windowMs;
  // Samples arrive in time order, so the last one at or before the start is the baseline.
  const baselineIndex = next.reduce((found, s, i) => (s.atMs <= windowStart ? i : found), 0);
  return next.slice(baselineIndex);
};

/** Loss over the samples, as a percentage rounded to 2 decimals. `0` until two polls exist. */
export const getWindowPacketLossPercent = (samples: PacketCountSample[]): number => {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last || first === last) return 0;

  // RTCP may revise `packetsLost` downwards; a negative delta is not a gain.
  const lost = Math.max(0, last.packetsLost - first.packetsLost);
  const received = Math.max(0, last.packetsReceived - first.packetsReceived);
  const total = lost + received;
  return total > 0 ? Math.round((lost / total) * 10000) / 100 : 0;
};

/**
 * How much a cumulative counter rose over the samples. Undefined until two polls exist, or when
 * the browser does not report the counter, so an unreported count never reads as `0`.
 */
export const getWindowCountDelta = ({ samples, select }: WindowCountArgs): number | undefined => {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last || first === last) return undefined;

  const from = select(first);
  const to = select(last);
  if (from == null || to == null) return undefined;
  // A reconnect restarts the counters; that is no evidence of requests.
  return Math.max(0, to - from);
};

/**
 * NACKs sent per packet received over the samples, as a percentage rounded to 2 decimals. A NACK
 * asks for one packet again, so this reads like loss the link recovered from. Undefined until two
 * polls exist, when NACKs are not reported, or when no packets arrived.
 */
export const getWindowNackPercent = (samples: PacketCountSample[]): number | undefined => {
  const nacks = getWindowCountDelta({ samples, select: s => s.nackCount });
  const received = getWindowCountDelta({ samples, select: s => s.packetsReceived });
  if (nacks == null || !received) return undefined;
  return Math.round((nacks / received) * 10000) / 100;
};

/** True while the connection is too new for its counters to say anything about the link. */
export const isWithinStartGrace = ({ connectedAtMs, nowMs, graceMs }: StartGraceArgs): boolean =>
  nowMs - connectedAtMs < graceMs;
